import * as Y from 'yjs'
import { prosemirrorJSONToYDoc, yDocToProsemirrorJSON } from 'y-prosemirror'
import { generateJSON } from '@tiptap/html'
import {
  type Extensions,
  getSchema,
  generateText,
  JSONContent,
} from '@tiptap/core'
import type { Node } from 'prosemirror-model'
import { isEmpty } from './is-empty'
import { transformationsCache } from './_cache'
import { proxyRichTextAssets } from '@basehub/utils/asset-proxy'
/**
 * Some blocks can be stored in a transient format prefixed with markers like
 * "!!RESET!!<real_state>".  These markers are *not* part of the Yjs / TipTap
 * state and must be ignored for all transformations.  This helper removes the
 * first marker it finds so the underlying transformation utilities receive a
 * clean state string.
 */
const stripTransformationPrefix = (state: string): string => {
  if (!state) return state
  //   e.g.   !!RESET!!<base64 or json>
  return state.replace(/^!![^!]+!!/, '')
}

/**
 * Unwrap media elements (images, videos) from paragraphs to allow them to be parsed as block nodes.
 * This handles cases like `<p><img src="..."></p>` or `<p><video src="..."></video>`
 * and converts them to just `<img src="...">` or `<video src="..."></video>`.
 *
 * Only unwraps paragraphs that contain ONLY media elements (and whitespace), not mixed content.
 */
const unwrapAssetsFromParagraphs = (html: string): string => {
  // Match <p> tags that contain only <img> or <video> tags (and whitespace)
  // This regex looks for: <p ...> followed by any combo of whitespace and media tags, then </p>
  const paragraphWithOnlyMediaRegex =
    /<p[^>]*>\s*((?:<img[^>]*>|<video[^>]*>(?:<\/video>)?|\s)+)\s*<\/p>/gi

  return html.replace(paragraphWithOnlyMediaRegex, (match, mediaContent) => {
    // Check if the content contains ONLY media tags and whitespace (no other text)
    const hasOnlyMedia =
      !/<\/?((?!img|video|p)[a-z])[^>]*>/i.test(mediaContent) &&
      !/[a-zA-Z0-9](?![^<]*>)/i.test(
        mediaContent.replace(/<[^>]+>/g, '').trim()
      )

    if (hasOnlyMedia) {
      // Return just the media elements without the paragraph wrapper
      return mediaContent.trim()
    }

    // If there's other content, leave it wrapped
    return match
  })
}

export const plainTextToYDoc = async (
  plainText: string,
  extensions: Extensions,
  opts?: { proxyAssets?: false | string; unwrapAssets?: boolean }
) => {
  // Preprocess HTML to unwrap media elements (images, videos) from paragraphs
  const preprocessedText = opts?.unwrapAssets
    ? unwrapAssetsFromParagraphs(plainText)
    : plainText

  let json = generateJSON(preprocessedText, extensions) as Node
  if (opts?.proxyAssets) {
    json = (await proxyRichTextAssets(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      json as any,
      opts.proxyAssets
    )) as unknown as typeof json
  }
  const schema = getSchema(extensions)
  const ydoc = prosemirrorJSONToYDoc(schema, json, 'content')
  return { ydoc, json, schema }
}

export const plainTextToYDocBase64State = async (
  plainText: string,
  extensions: Extensions,
  opts?: { proxyAssets?: false | string; unwrapAssets?: boolean }
) => {
  const { ydoc, json, schema } = await plainTextToYDoc(
    plainText,
    extensions,
    opts
  )
  const update = Y.encodeStateAsUpdateV2(ydoc)
  const base64 = Buffer.from(update).toString('base64')
  return { base64, ydoc, json, schema }
}

export const jsonToYDoc = (json: JSONContent, extensions: Extensions) => {
  const schema = getSchema(extensions)
  const ydoc = prosemirrorJSONToYDoc(schema, json, 'content')
  return { ydoc, json, schema }
}

export const jsonToYDocBase64State = (json: JSONContent, ext: Extensions) => {
  const { ydoc } = jsonToYDoc(json, ext)
  const update = Y.encodeStateAsUpdateV2(ydoc) // ✅ v2
  return { base64: Buffer.from(update).toString('base64'), ydoc, json }
}

export const yDocToPlainText = (yDoc: Y.Doc, extensions: Extensions) => {
  const prosemirrorJson = yDocToProsemirrorJSON(yDoc, 'content')
  const text = generateText(prosemirrorJson, extensions)

  return text
}

export const jsonToPlainText = (
  json: Record<string, unknown>,
  extensions: Extensions
) => {
  const text = generateText(json, extensions)
  return text
}

export const yDocToJson = (yDoc: Y.Doc) => {
  const prosemirrorJson = yDocToProsemirrorJSON(yDoc, 'content') as JSONContent
  return prosemirrorJson
}

export const stateToJson = (
  state: string,
  opts?: { cacheKey?: string; debug?: boolean }
): JSONContent => {
  // Remove any transformation prefix (e.g. "!!RESET!!")
  state = stripTransformationPrefix(state)
  if (opts?.debug) {
    console.log('stateToJson', state)
  }

  if (opts?.cacheKey) {
    const cached = transformationsCache.get('state-to-json' + opts.cacheKey)
    if (cached) {
      return cached as JSONContent
    }
  }

  /**
   * commenting this auto-cache for now because it introduced a bug (i think)
   */

  // const cached = transformationsCache.get('state-to-json' + state)
  // if (cached) {
  //   if (opts?.debug) {
  //     console.log('cached', JSON.stringify(cached))
  //   }
  //   return cached as JSONContent
  // }
  const result = normalizeJsonState(
    isJSONFormat(state)
      ? (JSON.parse(state) as any)
      : yDocToJson(buildYDocFromState(state))
  )
  if (opts?.cacheKey) {
    transformationsCache.set('state-to-json' + opts.cacheKey, result)
  }
  // transformationsCache.set('state-to-json' + state, result)
  if (opts?.debug) {
    console.log('result', JSON.stringify(result))
  }
  return result
}

export const stateToBase64 = (
  state: string,
  extensions: Extensions
): string => {
  state = stripTransformationPrefix(state)
  try {
    // block is committed
    const parsed = JSON.parse(state) as any
    return jsonToYDocBase64State(parsed, extensions).base64
  } catch (error) {
    return state
  }
}

export const buildYDocFromState = (state: string, options?: any) => {
  state = stripTransformationPrefix(state)
  const ydoc = new Y.Doc(options)

  if (state) {
    // convert string to unit8array:
    const unit8array = Buffer.from(state, 'base64')
    // apply update to ydoc
    try {
      Y.applyUpdateV2(ydoc, unit8array)
    } catch (error) {
      Y.applyUpdate(ydoc, unit8array)
    }
  }

  return ydoc
}

export const emptyTipTapContent = {
  type: 'doc',
  content: [
    {
      type: 'paragraph',
      content: [],
    },
  ],
}

/**
 * @name normalizeJsonState
 * @param {JSONContent} state
 * @description Normalize text in JSONContent format to be used in Yjs
 * @example normalizeJsonState(richText)
 * @returns {JSONContent}
 */
export const normalizeJsonState = (state: JSONContent) => {
  if (!state || isEmpty(state)) {
    return emptyTipTapContent
  }

  return mapJSONNode(state, (node): JSONContent => {
    if (node.type === 'text') {
      if (!node.text) {
        return []
      }
      return {
        ...node,
        /** Remove non-breaking spaces as browsers add these after pasting sometimes (https://discuss.prosemirror.net/t/non-breaking-spaces-being-added-to-pasted-html/3911/5) */
        text: node.text.replace(/\u00A0/g, ' '),
      }
    }
    return node
  })
}

/**
 * @description Check if a string is in JSON format
 * @example isJSONFormat(richText)
 * @returns {boolean}
 * @example isJSONFormat('{"type":"doc","content":[{"type":"paragraph","content":[{"type":"text","text":"Hello world"}]}]}') // true
 * @example isJSONFormat('Hello world') // false
 */

export const isJSONFormat = (state: string) => {
  state = stripTransformationPrefix(state)
  try {
    JSON.parse(state)
    return true
  } catch (error) {
    return false
  }
}

/**
 * @name mapJSONNode
 * @description Map a JSONContent node to mutate with a function
 *
 */

export const mapJSONNode = (
  node: JSONContent,
  fn: (node: JSONContent) => JSONContent
): JSONContent => {
  if (!node.content) {
    return fn(node)
  } else if (node.content.length === 0) {
    const newNode = fn(node)
    delete newNode.content
    return newNode
  }
  return {
    ...node,
    content: node.content
      .flatMap((child) => mapJSONNode(child, fn))
      .filter(Boolean),
  }
}
